import { Button } from '@/components/ui/button';
import { DialogFooter } from '@/components/ui/dialog';
import { type SelectableBank } from '../api/use-selectable-banks';
import { DocumentFigureRows } from '../components/document-figures';
import type { InvoiceRow } from '../types';
import { BankField, ReadOnlyRow } from './invoice-review-bank-field';
import { ReviewBodyFields } from './invoice-review-operator-fields';
import {
  blBodyRendersForKind,
  type EditState,
  type ReviewSystemValues,
} from './invoice-review-overrides';

// The read-only half of the review: the values the document renders today.
// The figures come from the render-context read (the same assembly the PDF
// binds), so a price fixed upstream after the draft was created shows here as
// it will print — not as the row last stored it.
export function SystemSection({ invoice }: Readonly<{ invoice: InvoiceRow }>) {
  return (
    <section aria-labelledby="review-system-heading" className="space-y-1">
      <h3
        id="review-system-heading"
        className="text-xs font-semibold uppercase text-muted-foreground"
      >
        From the system (read-only)
      </h3>
      <ReadOnlyRow label="Material" value={invoice.item_description} />
      <ReadOnlyRow label="Issuing entity" value={invoice.issuer_company_name} />
      <DocumentFigureRows invoiceId={invoice.id} />
      {invoice.order_id !== null ? (
        <p className="text-muted-foreground pt-1 text-xs">
          Counterparty, weight and price are set at the order level.{' '}
          <a href={`/inventory/${encodeURIComponent(invoice.order_id)}`} className="underline">
            Correct on the order
          </a>
        </p>
      ) : null}
    </section>
  );
}

export interface EditableSectionProps {
  readonly edit: EditState;
  readonly setEdit: (next: EditState) => void;
  // The per-key system resolution the fields are prefilled with and compared
  // against — a value equal to its system resolution stores no override.
  readonly system: ReviewSystemValues;
  // True until the render-context read settles and the fields are hydrated
  // with the values the document renders today.
  readonly isResolving: boolean;
  readonly banks: SelectableBank[];
  readonly isLoading: boolean;
  // The invoice currency, driving the bank catalog's empty-state message.
  readonly currency: string | null;
  // Sell side only: the issuing bank belongs to our entity catalog, which has
  // no meaning on a received (Purchase) invoice.
  readonly showBankField: boolean;
  // Whether the BL-body fields (material description + the operator-supplied
  // delivery/origin/contract fields) render for this invoice kind — only the
  // BL-based kinds bind them, so they are surfaced only where the edit actually
  // reaches the document, never as a field that silently vanishes.
  readonly canEditBlBodyFields: boolean;
}

export function EditableSection(props: Readonly<EditableSectionProps>) {
  const { edit, setEdit, system, isResolving, banks, isLoading } = props;
  return (
    <section aria-labelledby="review-editable-heading" className="space-y-4">
      <h3
        id="review-editable-heading"
        className="text-xs font-semibold uppercase text-muted-foreground"
      >
        Editable on this document
      </h3>
      {props.showBankField ? (
        <BankField
          banks={banks}
          isLoading={isLoading}
          bankId={edit.bankId}
          currency={props.currency}
          onBankChange={(bankId) => {
            setEdit({ ...edit, bankId });
          }}
        />
      ) : null}
      {props.canEditBlBodyFields ? (
        <ReviewBodyFields edit={edit} setEdit={setEdit} system={system} disabled={isResolving} />
      ) : null}
      <p className="text-xs text-muted-foreground">
        Editing the counterparty VAT id and tax-line text arrives with the finance field-split
        confirmation.
      </p>
    </section>
  );
}

function ReviewFooterButtons({
  isPending,
  isPreviewing,
  bankMissing,
  submitLabel,
  onCancel,
  onPreview,
}: Readonly<{
  isPending: boolean;
  isPreviewing: boolean;
  bankMissing: boolean;
  submitLabel: string;
  onCancel: () => void;
  onPreview: () => void;
}>) {
  const busy = isPending || isPreviewing;
  return (
    <>
      {/* The document renders a payment block, so previewing/generating without
          an issuing bank produces an incomplete document — block it with a
          visible reason (never hover-only). Saving the review stays allowed. */}
      {bankMissing ? (
        <p
          className="text-muted-foreground w-full self-center text-xs sm:mr-auto sm:w-auto"
          data-testid="review-preview-bank-hint"
        >
          Select an issuing bank to preview the document.
        </p>
      ) : null}
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      {/* Saves any pending edits, then opens a non-authoritative Draft preview
          so the operator sees the reviewed values on the actual document. */}
      <Button type="button" variant="secondary" disabled={busy || bankMissing} onClick={onPreview}>
        {isPreviewing ? 'Opening…' : 'Save & preview'}
      </Button>
      <Button type="submit" disabled={busy}>
        {isPending ? 'Saving…' : submitLabel}
      </Button>
    </>
  );
}

export interface ReviewFormProps {
  readonly invoice: InvoiceRow;
  readonly edit: EditState;
  readonly setEdit: (next: EditState) => void;
  readonly system: ReviewSystemValues;
  readonly isResolving: boolean;
  readonly banks: SelectableBank[];
  readonly isLoading: boolean;
  readonly isPending: boolean;
  readonly isPreviewing: boolean;
  // True when the INVOICING_ENTITY_BANK flag is on: renders the bank picker and
  // gates Save & preview on a bank selection. Off = no bank field, Save &
  // preview always available (the pre-catalog behavior).
  readonly bankPickerEnabled: boolean;
  // The primary-action label: 'Save review' standalone, 'Continue' when the
  // review runs as step 1 of the Issue flow.
  readonly submitLabel?: string;
  readonly onSubmit: () => void;
  readonly onPreview: () => void;
  readonly onCancel: () => void;
}

export function ReviewForm(props: Readonly<ReviewFormProps>) {
  const { invoice, edit, setEdit, banks, isLoading, isPending, isPreviewing } = props;
  const { onSubmit, onPreview, onCancel } = props;
  const showBankField = props.bankPickerEnabled && invoice.invoice_direction !== 'Purchase';
  const bankMissing = showBankField && edit.bankId === '';
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="flex min-h-0 flex-1 flex-col"
    >
      {/* The fields scroll within the capped dialog; the footer stays pinned so
          Cancel / Save & preview / Save review are always reachable. */}
      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto pr-1">
        <SystemSection invoice={invoice} />
        <div className="border-t" />
        <EditableSection
          edit={edit}
          setEdit={setEdit}
          system={props.system}
          isResolving={props.isResolving}
          banks={banks}
          isLoading={isLoading}
          currency={invoice.currency}
          showBankField={showBankField}
          canEditBlBodyFields={blBodyRendersForKind(invoice.invoice_type)}
        />
      </div>
      <DialogFooter className="mt-4 border-t pt-4">
        <ReviewFooterButtons
          isPending={isPending}
          isPreviewing={isPreviewing}
          bankMissing={bankMissing}
          submitLabel={props.submitLabel ?? 'Save review'}
          onCancel={onCancel}
          onPreview={onPreview}
        />
      </DialogFooter>
    </form>
  );
}
